import type { Authenticator } from '@ms/yammer-web-authenticators';
import { AdalRedirectAuthenticator, setAuthenticator } from '@ms/yammer-web-authenticators';
import { TeamsMeetingAuthenticator } from '@ms/yammer-web-authenticators/dist/teamsmeeting';
import { getMetaOsHubContextThrows, hasMetaOsHubContext } from '@ms/yammer-web-support/dist/metaOsHub';
import config from 'bootstrap/config';
import { isAnonymousUser } from 'teams';

import { getAuthTokenCache, initializeAuthTokenCache } from './authTokenCache';
import { isNAASupportAvailable } from './isNAASupportAvailable';
import { reportAuthError } from './reportAuthError';

type GetAuthenticator = () => Promise<Authenticator>;
const getAuthenticator: GetAuthenticator = async () => {
  if (!(await hasMetaOsHubContext())) {
    return new AdalRedirectAuthenticator(config.adal);
  }

  initializeAuthTokenCache();
  const isAnonymous = await isAnonymousUser();

  if (await isNAASupportAvailable()) {
    const shouldAutoRefresh = false;
    const context = await getMetaOsHubContextThrows();
    const tenant = config.adal.tenant;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config as any).adal = config.msal;

    const authenticatorOptions = {
      getAuthTokenCache,
      instance: config.adal.instance,
      clientId: config.adal.clientId,
      tenant,
      onError: reportAuthError,
      accountFilter: {
        homeAccountId: context.user?.id,
        tenantId: context.user?.tenant?.id,
        loginHint: context.user?.loginHint,
      },
      shouldAutoRefresh,
    };

    const { TeamsMeetingAuthenticatorV2 } = await import(
      /* webpackChunkName: "4-auth-naa" */ '@ms/yammer-web-authenticators/dist/teamsmeeting'
    );

    return new TeamsMeetingAuthenticatorV2(authenticatorOptions, isAnonymous);
  }

  return new TeamsMeetingAuthenticator(getAuthTokenCache, isAnonymous);
};

export type SetupAuthenticator = () => Promise<Authenticator>;
export const setupAuthenticator: SetupAuthenticator = async () => {
  const authenticator = await getAuthenticator();
  setAuthenticator(authenticator);

  return authenticator;
};

export default setupAuthenticator;
